import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, orderBy, getDocs, addDoc, setDoc, deleteDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
// signInAnonymously 제거 — 비로그인 상태 유지를 위해
import { db, auth } from '../firebase/config';
import { Building2, Phone, Mail, MapPin, Clock, Globe, Star, Briefcase, Image as ImageIcon, MessageSquare, ChevronLeft, CheckCircle, ArrowLeft, Lock, UserPlus, Heart, Camera, Handshake } from 'lucide-react';
import { createNotification } from '../services/NotificationSystem';
import { getOrCreateChatRoom } from '../utils/chatUtils';
import { getFeatures, isBetaUser } from '../utils/features';

function LabProfile({ user }) {
    // 단계별 오픈: 매칭(테스트보철·홍보페이지) CTA는 4차 오픈 또는 베타만
    const [featureFlags, setFeatureFlags] = useState(null);
    useEffect(() => {
        let alive = true;
        getFeatures().then(f => { if (alive) setFeatureFlags(f); });
        return () => { alive = false; };
    }, []);
    const matchOpen = isBetaUser(featureFlags, user) || ((featureFlags?.phase || 1) >= 4);
    const { labId } = useParams();
    const navigate = useNavigate();
    const [labData, setLabData] = useState(null);
    const [userData2, setUserData2] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('info');
    const [lightboxImg, setLightboxImg] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    // 🔒 접근 차단
    // - 비로그인 사용자: 로그인·회원가입 안내
    // - 기공소 사용자: 타 기공소 상세 프로필 열람 불가 (본인 제외)
    const isLoggedInUser = !!user?.uid;
    // 🌏 해외 기공소는 경쟁자가 아니라 발주 고객 — 국내 기공소 차단 정책에서 제외
    const isOverseasViewer = user?.labKind === 'overseas';
    const isLabUser = user?.businessType === 'lab' && !isOverseasViewer;
    const isOwnProfile = user?.businessType === 'lab' && user?.uid && (user.uid === labId);
    const isGuestBlocked = !isLoggedInUser;
    const isLabBlocked = isLabUser && !isOwnProfile;
    const isBlocked = isGuestBlocked || isLabBlocked;

    // ── 거래처 연결요청 (설정>거래처 관리와 같은 connections 컬렉션 재사용) ──
    // connStatus: null(없음) | 'pending'(내가 보냄) | 'received'(상대가 보냄) | 'accepted'(연결됨)
    const [connStatus, setConnStatus] = useState(null);
    const [connDocId, setConnDocId] = useState(null);
    const [connSending, setConnSending] = useState(false);
    const canConnect = isLoggedInUser && !isLabUser && user?.uid !== labId;

    useEffect(() => {
        if (!canConnect || !labId) return;
        (async () => {
            try {
                const ref = collection(db, 'connections');
                // 회사 소속이면 회사 ID 기준 + 과거 개인 uid 기록도 함께 조회 (하위 호환)
                const myIds = [...new Set([user?.companyId, user?.uid].filter(Boolean))];
                const snaps = await Promise.all(myIds.flatMap(id => [
                    getDocs(query(ref, where('requesterId', '==', id), where('receiverId', '==', labId))),
                    getDocs(query(ref, where('requesterId', '==', labId), where('receiverId', '==', id))),
                ]));
                const sentDocs = snaps.filter((_, i) => i % 2 === 0).flatMap(s => s.docs);
                const recvDocs = snaps.filter((_, i) => i % 2 === 1).flatMap(s => s.docs);
                const all = [...sentDocs, ...recvDocs].map(d => ({ id: d.id, ...d.data() }));
                const accepted = all.find(c => c.status === 'accepted');
                const sentPending = sentDocs.map(d => ({ id: d.id, ...d.data() })).find(c => c.status === 'pending');
                const recvPending = recvDocs.map(d => ({ id: d.id, ...d.data() })).find(c => c.status === 'pending');
                if (accepted) { setConnStatus('accepted'); setConnDocId(accepted.id); }
                else if (recvPending) { setConnStatus('received'); setConnDocId(recvPending.id); }
                else if (sentPending) { setConnStatus('pending'); setConnDocId(sentPending.id); }
            } catch (e) { console.warn('연결 상태 확인 실패:', e); }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [labId, user?.uid]);

    const handleConnectionRequest = async () => {
        if (!canConnect || connSending) return;
        try {
            setConnSending(true);
            if (connStatus === 'received' && connDocId) {
                // 상대(기공소)가 이미 나에게 요청한 상태 → 바로 수락 (ConnectionList와 동일 동작)
                await updateDoc(doc(db, 'connections', connDocId), {
                    status: 'accepted', updatedAt: new Date(), acceptedBy: user.uid,
                });
                setConnStatus('accepted');
                alert('기공소가 이미 연결 요청을 보낸 상태여서 바로 연결되었습니다!');
                return;
            }
            // 새 요청 — 회사 ID 기준 + 쌍 고정 ID (중복 차단), ConnectionList.js와 호환
            const ownerId = user.companyId || user.uid;
            const connId = [ownerId, labId].sort().join('_');
            await setDoc(doc(db, 'connections', connId), {
                requesterId: ownerId,
                receiverId: labId,
                recipientId: labId,
                status: 'pending',
                createdAt: new Date(),
                requesterName: user.businessName || user.companyName || user.name || '',
                requesterType: user.businessType || 'dental',
                requestedByUid: user.uid,
                source: 'lab-profile',
            }, { merge: true });
            setConnStatus('pending');
            // 대상 기공소에 알림 (해외 발주 요청 포함)
            try {
                await createNotification({
                    recipientId: labId,
                    type: 'connection_request',
                    title: isOverseasViewer ? '해외 기공소 연결 요청' : '거래처 연결 요청',
                    message: `${user.businessName || user.companyName || user.name || '업체'}에서 거래처 연결을 요청했습니다.${isOverseasViewer ? ' (해외 발주 문의)' : ''}`,
                    link: '/connections',
                    metadata: { requesterId: ownerId, overseas: !!isOverseasViewer },
                });
            } catch (e) { console.warn('연결 요청 알림 실패:', e); }
            alert(isOverseasViewer
                ? 'Partner request sent. You will be connected once the lab accepts.'
                : '연결 요청을 보냈습니다.\n기공소가 수락하면 [설정 > 거래처 관리]에서 확인됩니다.');
        } catch (e) {
            console.error('연결 요청 실패:', e);
            alert('연결 요청에 실패했습니다.');
        } finally {
            setConnSending(false);
        }
    };

    // 🤝 덴클에 문의하기 — 연결은 덴탈클러스터가 직접 받아 중개 (2026-08-14 대표 확정)
    //   기존의 A/S상담·거래처 연결요청·직접 전화 버튼을 이 한 버튼으로 대체.
    //   matchRequests 컬렉션에 기록 → 관리자 [거래문의 관리] 화면에서 접수·상담·매칭 처리.
    const [inquirySending, setInquirySending] = useState(false);
    const [inquirySent, setInquirySent] = useState(false);
    const handleDenclInquiry = async () => {
        if (inquirySending || inquirySent) return;
        try {
            setInquirySending(true);
            await addDoc(collection(db, 'matchRequests'), {
                // 요청자(문의한 치과/업체)
                clinicId: user.companyId || user.uid,
                clinicName: user.businessName || user.companyName || user.name || '',
                clinicCompany: user.companyName || user.businessName || '',
                clinicPhone: user.phone || user.contactPhone || '',
                clinicEmail: user.email || '',
                clinicType: user.businessType || 'dental',
                requestedByUid: user.uid,
                // 문의 대상 기공소
                labId,
                labName: businessName,
                labBizName: businessName,
                labContactPhone: contactPhone || '',
                labContactEmail: contactEmail || '',
                labAddress: address || '',
                // 문의 메타
                message: `[기공소 상세 문의] ${user.businessName || user.companyName || user.name || '업체'} → ${businessName} 연결 요청`,
                status: 'pending',
                source: 'lab-profile',
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            });
            setInquirySent(true);
            // 관리자에게 알림 (실패해도 문의 자체는 접수됨)
            try {
                await createNotification({
                    recipientId: 'admin',
                    type: 'match_request',
                    title: '기공소 연결 문의 접수',
                    message: `${user.businessName || user.companyName || user.name || '업체'}에서 ${businessName} 연결을 문의했습니다.`,
                    link: '/admin/match-requests',
                    metadata: { labId, clinicId: user.companyId || user.uid },
                });
            } catch (e) { console.warn('관리자 알림 실패:', e); }
        } catch (e) {
            console.error('덴클 문의 실패:', e);
            alert('문의 접수에 실패했습니다. 잠시 후 다시 시도해주세요.');
        } finally {
            setInquirySending(false);
        }
    };

    // Portfolio posts states
    const [labPosts, setLabPosts] = useState([]);
    const [labPostLikes, setLabPostLikes] = useState(new Set());

    // Review states
    const [reviews, setReviews] = useState([]);
    const [showReviewForm, setShowReviewForm] = useState(false);
    const [reviewForm, setReviewForm] = useState({ rating: 5, qualityRating: 5, deliveryRating: 5, communicationRating: 5, comment: '' });
    const [submittingReview, setSubmittingReview] = useState(false);
    const [averageRating, setAverageRating] = useState(0);

    // Map refs (must be before any early returns)
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);

    // 카카오맵 초기화 (데이터 로드 후, DOM 준비 대기)
    useEffect(() => {
        if (mapInstanceRef.current) return;
        const addr = labData?.address || userData2?.address || '';
        const name = labData?.businessName || userData2?.businessName || '기공소';
        if (!addr) return;

        const initMap = () => {
            if (!mapRef.current) return false;
            const kakao = window.kakao;
            if (!kakao?.maps) return false;

            kakao.maps.load(() => {
                if (!mapRef.current || mapInstanceRef.current) return;
                const geocoder = new kakao.maps.services.Geocoder();
                geocoder.addressSearch(addr, (result, status) => {
                    if (!mapRef.current) return;
                    if (status === kakao.maps.services.Status.OK) {
                        const coords = new kakao.maps.LatLng(result[0].y, result[0].x);
                        const map = new kakao.maps.Map(mapRef.current, { center: coords, level: 3 });
                        const marker = new kakao.maps.Marker({ map, position: coords });
                        const iw = new kakao.maps.InfoWindow({
                            content: `<div style="padding:6px 12px;font-size:13px;font-weight:600;white-space:nowrap;">${name}</div>`
                        });
                        iw.open(map, marker);
                        mapInstanceRef.current = map;
                    } else {
                        const ps = new kakao.maps.services.Places();
                        ps.keywordSearch(addr, (data, s2) => {
                            if (!mapRef.current) return;
                            if (s2 === kakao.maps.services.Status.OK && data.length > 0) {
                                const coords = new kakao.maps.LatLng(data[0].y, data[0].x);
                                const map = new kakao.maps.Map(mapRef.current, { center: coords, level: 3 });
                                new kakao.maps.Marker({ map, position: coords });
                                mapInstanceRef.current = map;
                            }
                        });
                    }
                });
            });
            return true;
        };

        // DOM이 준비될 때까지 재시도
        if (!initMap()) {
            const timer = setTimeout(() => initMap(), 300);
            const timer2 = setTimeout(() => initMap(), 1000);
            const timer3 = setTimeout(() => initMap(), 2000);
            return () => { clearTimeout(timer); clearTimeout(timer2); clearTimeout(timer3); };
        }
    }, [labData, userData2, loading]);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        // 🔒 차단된 사용자(타 기공소 회원)는 데이터 로드 자체를 스킵
        if (isBlocked) {
            setLoading(false);
            return;
        }
        const loadData = async () => {
            try {
                setLoading(true);
                // Load labAdvertisements
                const adRef = doc(db, 'labAdvertisements', labId);
                const adSnap = await getDoc(adRef);
                let ad = null;
                if (adSnap.exists()) {
                    ad = { id: adSnap.id, ...adSnap.data() };
                }

                // Load user data for fallback fields
                const userRef = doc(db, 'users', labId);
                const userSnap = await getDoc(userRef);
                let userDoc = null;
                if (userSnap.exists()) {
                    userDoc = { id: userSnap.id, ...userSnap.data() };
                    setUserData2(userDoc);
                }

                // 홍보 미등록 기공소도 users 문서가 있으면 기본 프로필 표시 (not-found 방지)
                if (!ad && userDoc) {
                    ad = { id: labId, businessName: userDoc.businessName || userDoc.name || '' };
                }
                setLabData(ad);

                // 방문 기록 (유입 수만 기록) — 실재하는 기공소만
                if (ad || userDoc) {
                    try {
                        await addDoc(collection(db, 'qrVisits'), {
                            labId: labId,
                            labName: ad?.businessName || '',
                            visitedAt: serverTimestamp(),
                            referrer: (document.referrer || '').slice(0, 500), // rules 500자 상한(2026-10-11)
                            page: '/lab/' + labId,
                        });
                    } catch (e) {
                        console.log('방문 기록 실패:', e);
                    }
                }

                // Load reviews
                const reviewsRef = collection(db, 'labReviews');
                const reviewsQ = query(reviewsRef, where('labId', '==', labId));
                const reviewsSnap = await getDocs(reviewsQ);
                const reviewsData = reviewsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
                reviewsData.sort((a, b) => (b.createdAt?.toDate?.() || 0) - (a.createdAt?.toDate?.() || 0));
                setReviews(reviewsData);
                if (reviewsData.length > 0) {
                    setAverageRating((reviewsData.reduce((s, r) => s + (r.rating || 0), 0) / reviewsData.length).toFixed(1));
                }

                // Load portfolio posts
                try {
                    const postsQ = query(collection(db, 'labPosts'), where('labId', '==', labId), orderBy('createdAt', 'desc'));
                    const postsSnap = await getDocs(postsQ);
                    setLabPosts(postsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
                    // Load user likes
                    if (auth.currentUser) {
                        const likesQ = query(collection(db, 'labPostLikes'), where('userId', '==', auth.currentUser.uid));
                        const likesSnap = await getDocs(likesQ);
                        setLabPostLikes(new Set(likesSnap.docs.map(d => d.data().postId)));
                    }
                } catch (e) { console.log('포트폴리오 게시물 로드:', e); }
            } catch (error) {
                console.error('기공소 프로필 로드 실패:', error);
            } finally {
                setLoading(false);
            }
        };
        if (labId) loadData();
    }, [labId]);

    const formatPrice = (price) => {
        if (!price && price !== 0) return '-';
        const num = typeof price === 'string' ? parseInt(price.replace(/[^0-9]/g, ''), 10) : price;
        if (isNaN(num)) return price;
        if (num >= 10000) {
            const man = Math.floor(num / 10000);
            const rest = num % 10000;
            if (rest >= 1000) {
                const cheon = Math.floor(rest / 1000);
                return `${man}만${cheon}천원`;
            }
            if (rest > 0) return `${man}만${rest.toLocaleString()}원`;
            return `${man}만원`;
        }
        return `${num.toLocaleString()}원`;
    };

    const val = (field) => {
        if (labData && labData[field]) return labData[field];
        if (userData2 && userData2[field]) return userData2[field];
        return null;
    };

    const handleSubmitReview = async () => {
        if (!user) { navigate('/login'); return; }
        if (!reviewForm.comment.trim()) { alert('리뷰 내용을 입력해주세요.'); return; }
        try {
            setSubmittingReview(true);
            const userDoc = await getDoc(doc(db, 'users', user.uid));
            const userData = userDoc.data();
            await addDoc(collection(db, 'labReviews'), {
                labId,
                reviewerId: user.uid,
                reviewerName: userData?.businessName || userData?.name || '익명',
                ...reviewForm,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            });
            // reload reviews
            const reviewsQ2 = query(collection(db, 'labReviews'), where('labId', '==', labId));
            const snap2 = await getDocs(reviewsQ2);
            const newReviews = snap2.docs.map(d => ({ id: d.id, ...d.data() }));
            newReviews.sort((a, b) => (b.createdAt?.toDate?.() || 0) - (a.createdAt?.toDate?.() || 0));
            setReviews(newReviews);
            if (newReviews.length > 0) setAverageRating((newReviews.reduce((s, r) => s + (r.rating || 0), 0) / newReviews.length).toFixed(1));
            setShowReviewForm(false);
            setReviewForm({ rating: 5, qualityRating: 5, deliveryRating: 5, communicationRating: 5, comment: '' });
        } catch (e) {
            console.error('리뷰 작성 실패:', e);
            alert('리뷰 작성에 실패했습니다.');
        } finally {
            setSubmittingReview(false);
        }
    };

    // Star rendering helper
    const renderStars = (rating, size = 18, onClick = null) => {
        return (
            <div style={S.starRow}>
                {[1, 2, 3, 4, 5].map(i => (
                    <Star
                        key={i}
                        size={size}
                        color={i <= rating ? '#f59e0b' : '#d1d5db'}
                        fill={i <= rating ? '#f59e0b' : 'none'}
                        style={onClick ? { cursor: 'pointer' } : {}}
                        onClick={onClick ? () => onClick(i) : undefined}
                    />
                ))}
            </div>
        );
    };

    if (isBlocked) {
        // 비로그인자 → 로그인·회원가입 안내
        if (isGuestBlocked) {
            return (
                <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', background: '#f8fafc' }}>
                    <div style={{ maxWidth: 460, width: '100%', background: '#fff', borderRadius: 20, padding: '40px 28px', textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
                        <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'linear-gradient(135deg, #eef2ff, #e0e7ff)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                            <Lock size={32} color="#6366f1" />
                        </div>
                        <h2 style={{ fontSize: 22, fontWeight: 900, color: '#0f172a', marginBottom: 12 }}>로그인이 필요합니다</h2>
                        <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.7, marginBottom: 28 }}>
                            기공소 상세 프로필은 <strong style={{ color: '#0f172a' }}>치과 회원</strong>만<br />
                            열람할 수 있습니다.<br />
                            <span style={{ fontSize: 12, color: '#94a3b8' }}>단가·사진·연락처 등 영업정보 보호를 위한 정책입니다.</span>
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <button onClick={() => navigate('/signup?type=clinic')} style={{ width: '100%', padding: '14px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 800, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                                <UserPlus size={16} /> 치과 회원가입
                            </button>
                            <button onClick={() => navigate('/login')} style={{ width: '100%', padding: '14px', background: '#fff', color: '#6366f1', border: '1.5px solid #c7d2fe', borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                                이미 회원이신가요? 로그인
                            </button>
                            <button onClick={() => navigate(-1)} style={{ width: '100%', padding: '10px', background: 'transparent', color: '#94a3b8', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginTop: 4 }}>
                                뒤로가기
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        // 기공소 사용자 → 타 기공소 열람 불가 안내
        return (
            <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', background: '#f8fafc' }}>
                <div style={{ maxWidth: 440, width: '100%', background: '#fff', borderRadius: 20, padding: '40px 28px', textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
                    <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                        <Lock size={28} color="#ef4444" />
                    </div>
                    <h2 style={{ fontSize: 20, fontWeight: 900, color: '#0f172a', marginBottom: 10 }}>접근 제한</h2>
                    <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.7, marginBottom: 28 }}>
                        기공소 회원은 다른 기공소의 상세 프로필을<br />
                        열람할 수 없습니다.<br />
                        <span style={{ fontSize: 12, color: '#94a3b8' }}>단가·포트폴리오 등 영업정보 보호를 위한 정책입니다.</span>
                    </p>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                        {matchOpen && (
                            <button onClick={() => navigate('/lab-advertising')} style={{ padding: '12px 20px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                                내 홍보페이지
                            </button>
                        )}
                        <button onClick={() => navigate(-1)} style={{ padding: '12px 20px', background: '#fff', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                            뒤로가기
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div style={S.loadingWrap}>
                <div style={S.spinner} />
                <p style={{ color: '#64748b' }}>기공소 프로필을 불러오는 중...</p>
            </div>
        );
    }

    if (!labData) {
        return (
            <div style={S.loadingWrap}>
                <Building2 size={56} color="#cbd5e1" />
                <p style={{ margin: '16px 0 0', fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>기공소 정보를 찾을 수 없습니다</p>
                <button onClick={() => navigate(-1)} style={{ ...S.btnBack, marginTop: '20px' }}>
                    <ArrowLeft size={16} /> 뒤로가기
                </button>
            </div>
        );
    }

    const lab = labData;
    const businessName = val('businessName') || '기공소';
    const slogan = lab.slogan || '';
    const representative = lab.representative || '';
    const foundingYear = lab.foundingYear || '';
    const introduction = lab.introduction || lab.philosophy || lab.detailedIntro || '';
    const detailedIntro = lab.detailedIntro || lab.philosophy || '';
    const specialties = Array.isArray(lab.specialties) ? lab.specialties : (Array.isArray(lab.services) ? lab.services.map(s => typeof s === 'string' ? s : s.name) : []);
    const priceTable = Array.isArray(lab.priceTable) ? lab.priceTable : [];
    const portfolioImages = Array.isArray(lab.portfolioImages) ? lab.portfolioImages : [];
    const studioImages = Array.isArray(lab.studioImages) ? lab.studioImages : [];
    const compatibleScanners = Array.isArray(lab.compatibleScanners) ? lab.compatibleScanners : [];
    const deliveryRegions = Array.isArray(lab.deliveryRegions) ? lab.deliveryRegions : [];
    // firstDealBenefit(첫 거래 할인) 표시는 의료법 가이드에 따라 제거됨 (2026-07-19)
    const remakeRate = lab.remakeRate;
    const monthlyCapacity = lab.monthlyCapacity;
    const contactPhone = lab.contactPhone || lab.phone || val('phone') || '';
    const contactEmail = lab.contactEmail || lab.email || val('email') || '';
    const contactWebsite = lab.contactWebsite || '';

    // 로그인 여부: 실제 회원이면 단가표·연락처 공개
    const isLoggedIn = !!user && !!user.uid && !user.isAnonymous;

    const renderSignupBanner = (message) => (
        <div style={{
            padding: '20px 24px', borderRadius: 12,
            background: 'linear-gradient(135deg, #eef2ff, #e0e7ff)',
            border: '1px solid #c7d2fe',
            textAlign: 'center', margin: '8px 0',
        }}>
            <Lock size={24} color="#6366f1" style={{ marginBottom: 8 }} />
            <div style={{ fontSize: 15, fontWeight: 800, color: '#3730a3', marginBottom: 6 }}>{message}</div>
            <div style={{ fontSize: 12, color: '#6366f1', marginBottom: 14 }}>무료 회원가입 후 확인할 수 있습니다</div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                <button onClick={() => window.location.href = '/signup'} style={{
                    padding: '10px 24px', backgroundColor: '#6366f1', color: '#fff',
                    border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 700,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                }}>
                    <UserPlus size={14} /> 무료 가입하기
                </button>
                <button onClick={() => window.location.href = '/login'} style={{
                    padding: '10px 24px', backgroundColor: '#fff', color: '#6366f1',
                    border: '1px solid #c7d2fe', borderRadius: 10, fontSize: 13, fontWeight: 700,
                    cursor: 'pointer',
                }}>
                    로그인
                </button>
            </div>
        </div>
    );
    const workingHours = lab.workingHours || '';
    const address = val('address') || '';
    const profileImageUrl = lab.profileImageUrl || '';

    // ── Tab content renderers ──

    const renderInfoTab = () => (
        <div>
            {/* 소개는 위에서 로고 옆에 이미 표시됨 — 상세 소개만 표시 */}
            {detailedIntro && detailedIntro !== introduction && (
                <div style={S.section}>
                    <h3 style={S.sectionTitle}>
                        <Building2 size={20} color="#6366f1" /> 상세 소개
                    </h3>
                    <p style={S.sectionText}>{detailedIntro}</p>
                </div>
            )}

            {/* Price table — 정책상 가격 비공개 (일단 숨김, 되살리려면 false 제거) */}
            {false && priceTable.length > 0 && (
                <div style={S.section}>
                    <h3 style={S.sectionTitle}>
                        <Star size={20} color="#6366f1" /> 단가표
                        {!isLoggedIn && <Lock size={14} color="#94a3b8" style={{ marginLeft: 6 }} />}
                    </h3>
                    {isLoggedIn ? (
                        <div style={S.tableWrap}>
                            <table style={S.table}>
                                <thead>
                                    <tr>
                                        <th style={S.th}>항목</th>
                                        <th style={S.th}>단가</th>
                                        <th style={S.th}>납기</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {priceTable.map((row, i) => (
                                        <tr key={i}>
                                            <td style={S.td}>{row.item || row.name || '-'}</td>
                                            <td style={S.td}>{formatPrice(row.price)}</td>
                                            <td style={S.td}>{row.deliveryDays ? `${row.deliveryDays}일` : '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        renderSignupBanner('단가표를 확인하려면 가입이 필요합니다')
                    )}
                </div>
            )}

            {/* 첫 거래 혜택(할인) 배너 — 의료법 가이드(할인 표시 금지)에 따라 제거 (2026-07-19) */}

            {/* 지도 */}
            {address && !isOverseasViewer && (
                <div style={S.section}>
                    <h3 style={S.sectionTitle}>
                        <MapPin size={20} color="#6366f1" /> 위치
                    </h3>
                    <div ref={mapRef} style={S.mapContainer} />
                    <div style={S.mapAddress}>
                        <MapPin size={14} color="#64748b" />
                        <span>{address}</span>
                    </div>
                </div>
            )}
        </div>
    );

    const renderPortfolioTab = () => (
        <div>
            {portfolioImages.length === 0 && studioImages.length === 0 ? (
                <div style={S.emptyState}>
                    <ImageIcon size={48} color="#cbd5e1" />
                    <p style={S.emptyText}>아직 포트폴리오가 없습니다</p>
                </div>
            ) : (
                <>
                    {/* 작업물 사진 */}
                    {portfolioImages.length > 0 && (
                        <div style={{ marginBottom: '32px' }}>
                            <h3 style={S.sectionTitle}><Briefcase size={18} color="#6366f1" /> 작업물 사진</h3>
                            <div style={S.portfolioGrid}>
                                {portfolioImages.map((url, i) => (
                                    <div key={i} style={S.portfolioItem} onClick={() => setLightboxImg(url)}>
                                        <img src={url} alt={`작업물 ${i + 1}`} style={S.portfolioImg} />
                                        <div style={S.portfolioOverlay}>
                                            <ImageIcon size={20} color="#fff" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* 기공소 내부사진 */}
                    {studioImages.length > 0 && (
                        <div>
                            <h3 style={S.sectionTitle}><Building2 size={18} color="#6366f1" /> 기공소 내부</h3>
                            <div style={S.portfolioGrid}>
                                {studioImages.map((url, i) => (
                                    <div key={i} style={S.portfolioItem} onClick={() => setLightboxImg(url)}>
                                        <img src={url} alt={`내부 ${i + 1}`} style={S.portfolioImg} />
                                        <div style={S.portfolioOverlay}>
                                            <ImageIcon size={20} color="#fff" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );

    const togglePostLike = async (post) => {
        if (!auth.currentUser) { navigate('/signup'); return; }
        const uid = auth.currentUser.uid;
        const likeId = `${uid}_${post.id}`;
        try {
            if (labPostLikes.has(post.id)) {
                await deleteDoc(doc(db, 'labPostLikes', likeId));
                setLabPostLikes(prev => { const s = new Set(prev); s.delete(post.id); return s; });
                setLabPosts(prev => prev.map(p => p.id === post.id ? { ...p, likes: Math.max(0, (p.likes || 1) - 1) } : p));
                const { updateDoc } = await import('firebase/firestore');
                await updateDoc(doc(db, 'labPosts', post.id), { likes: Math.max(0, (post.likes || 1) - 1) });
            } else {
                await setDoc(doc(db, 'labPostLikes', likeId), { userId: uid, postId: post.id, createdAt: serverTimestamp() });
                setLabPostLikes(prev => new Set([...prev, post.id]));
                setLabPosts(prev => prev.map(p => p.id === post.id ? { ...p, likes: (p.likes || 0) + 1 } : p));
                const { updateDoc } = await import('firebase/firestore');
                await updateDoc(doc(db, 'labPosts', post.id), { likes: (post.likes || 0) + 1 });
            }
        } catch (e) { console.error('좋아요 실패:', e); }
    };

    const renderPostsTab = () => (
        <div>
            {labPosts.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8' }}>
                    <Camera size={36} color="#d1d5db" />
                    <p style={{ marginTop: '8px', fontSize: '14px' }}>아직 게시물이 없습니다</p>
                </div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '4px' }}>
                    {labPosts.map(post => (
                        <div key={post.id} style={{ position: 'relative', aspectRatio: '1', overflow: 'hidden', borderRadius: '4px', cursor: 'pointer' }}
                            onClick={() => setLightboxImg(post.photos?.[0])}>
                            <img src={post.photos?.[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(transparent, rgba(0,0,0,0.6))', padding: '24px 8px 6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ color: '#fff', fontSize: '10px', fontWeight: '600' }}>{post.category}</span>
                                <button onClick={(e) => { e.stopPropagation(); togglePostLike(post); }}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '3px', color: '#fff', fontSize: '11px' }}>
                                    <Heart size={12} fill={labPostLikes.has(post.id) ? '#ef4444' : 'none'} color={labPostLikes.has(post.id) ? '#ef4444' : '#fff'} />
                                    {post.likes || 0}
                                </button>
                            </div>
                            {post.photos?.length > 1 && (
                                <div style={{ position: 'absolute', top: '4px', right: '4px', background: 'rgba(0,0,0,0.5)', color: '#fff', padding: '1px 6px', borderRadius: '6px', fontSize: '9px', fontWeight: '700' }}>
                                    +{post.photos.length - 1}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    const renderReviewTab = () => (
        <div>
            {/* Average rating summary */}
            <div style={S.reviewSummary}>
                <div style={{ fontSize: '48px', fontWeight: '800', color: '#0f172a', lineHeight: '1' }}>
                    {reviews.length > 0 ? averageRating : '-'}
                </div>
                <div style={{ margin: '8px 0' }}>
                    {reviews.length > 0 ? renderStars(Math.round(averageRating), 24) : renderStars(0, 24)}
                </div>
                <div style={{ fontSize: '14px', color: '#64748b', fontWeight: '500' }}>
                    {reviews.length > 0 ? `${reviews.length}개 리뷰` : '아직 리뷰가 없습니다'}
                </div>
            </div>

            {/* Write review button */}
            {user && user.uid !== labId && (
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                    <button
                        style={S.btnWriteReview}
                        onClick={() => setShowReviewForm(!showReviewForm)}
                    >
                        <Star size={16} /> {showReviewForm ? '취소' : '리뷰 작성'}
                    </button>
                </div>
            )}

            {/* Review form */}
            {showReviewForm && (
                <div style={S.reviewForm}>
                    <h4 style={{ margin: '0 0 20px', fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>리뷰 작성</h4>

                    {/* Overall rating */}
                    <div style={S.ratingRow}>
                        <span style={S.ratingLabel}>전체 평점</span>
                        {renderStars(reviewForm.rating, 28, (val) => setReviewForm(prev => ({ ...prev, rating: val })))}
                        <span style={S.ratingValue}>{reviewForm.rating}</span>
                    </div>

                    {/* Category ratings — 가격 만족도는 의료법 가이드(가격 표시 금지)에 따라 제거 (2026-07-19) */}
                    {[
                        { key: 'qualityRating', label: '품질' },
                        { key: 'deliveryRating', label: '납기 준수' },
                        { key: 'communicationRating', label: '소통' },
                    ].map(cat => (
                        <div key={cat.key} style={S.ratingRow}>
                            <span style={S.ratingLabel}>{cat.label}</span>
                            {renderStars(reviewForm[cat.key], 22, (val) => setReviewForm(prev => ({ ...prev, [cat.key]: val })))}
                            <span style={S.ratingValue}>{reviewForm[cat.key]}</span>
                        </div>
                    ))}

                    {/* Comment */}
                    <textarea
                        style={S.reviewTextarea}
                        placeholder="리뷰 내용을 입력해주세요..."
                        value={reviewForm.comment}
                        onChange={e => setReviewForm(prev => ({ ...prev, comment: e.target.value }))}
                        rows={4}
                    />

                    {/* Submit / Cancel */}
                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                        <button
                            style={S.btnCancelReview}
                            onClick={() => { setShowReviewForm(false); setReviewForm({ rating: 5, qualityRating: 5, deliveryRating: 5, communicationRating: 5, comment: '' }); }}
                        >
                            취소
                        </button>
                        <button
                            style={S.btnSubmitReview}
                            onClick={handleSubmitReview}
                            disabled={submittingReview}
                        >
                            {submittingReview ? '제출 중...' : '리뷰 등록'}
                        </button>
                    </div>
                </div>
            )}

            {/* Review list */}
            {reviews.length === 0 && !showReviewForm && (
                <div style={S.emptyState}>
                    <MessageSquare size={48} color="#cbd5e1" />
                    <p style={S.emptyText}>아직 리뷰가 없습니다</p>
                    <p style={{ fontSize: '13px', color: '#94a3b8', margin: '4px 0 0' }}>첫 번째 리뷰를 남겨보세요</p>
                </div>
            )}

            {reviews.map(review => (
                <div key={review.id} style={S.reviewCard}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                        <div>
                            <div style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>{review.reviewerName || '익명'}</div>
                            <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '2px' }}>
                                {review.createdAt?.toDate ? review.createdAt.toDate().toLocaleDateString('ko-KR') : ''}
                            </div>
                        </div>
                        <div>{renderStars(review.rating || 0, 16)}</div>
                    </div>

                    {/* Category rating badges */}
                    <div style={S.ratingBadges}>
                        <span style={S.ratingBadge}>품질 {review.qualityRating || '-'}</span>
                        <span style={S.ratingBadgeDivider}>|</span>
                        <span style={S.ratingBadge}>납기 {review.deliveryRating || '-'}</span>
                        <span style={S.ratingBadgeDivider}>|</span>
                        <span style={S.ratingBadge}>소통 {review.communicationRating || '-'}</span>
                    </div>

                    {/* Comment */}
                    {review.comment && (
                        <p style={{ margin: '12px 0 0', fontSize: '14px', color: '#475569', lineHeight: '1.7', whiteSpace: 'pre-wrap' }}>
                            {review.comment}
                        </p>
                    )}
                </div>
            ))}
        </div>
    );

    // ── Sidebar content ──
    const renderSidebar = () => (
        <div style={isMobile ? S.sidebarMobile : S.sidebar}>
            {/* Business name & slogan */}
            <h1 style={S.sidebarName}>{businessName}</h1>
            {slogan && <p style={S.sidebarSlogan}>{slogan}</p>}

            {/* Average rating in sidebar — 의료법 가이드에 따라 소유자에게만 노출 */}
            {user?.uid === labId && reviews.length > 0 && (
                <div style={S.sidebarRating} onClick={() => setActiveTab('review')}>
                    <Star size={16} color="#f59e0b" fill="#f59e0b" />
                    <span style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>{averageRating}</span>
                    <span style={{ fontSize: '13px', color: '#64748b' }}>({reviews.length}개 리뷰)</span>
                </div>
            )}

            {/* Meta */}
            <div style={S.sidebarMeta}>
                {foundingYear && <span style={S.metaItem}>설립 {foundingYear}년</span>}
                {representative && <span style={S.metaItem}>대표 {representative}</span>}
            </div>

            {/* Stats badges */}
            <div style={S.statsBadges}>
                {remakeRate !== undefined && remakeRate !== null && remakeRate !== '' && (
                    <div style={S.statBadge}>
                        <CheckCircle size={14} color="#10b981" />
                        <span>리메이크율 {remakeRate}%</span>
                    </div>
                )}
                {monthlyCapacity && (
                    <div style={S.statBadge}>
                        <Briefcase size={14} color="#6366f1" />
                        <span>월 {monthlyCapacity}건 처리</span>
                    </div>
                )}
            </div>

            {/* 단가표 (사이드바) — 정책상 가격 비공개 (일단 숨김, 되살리려면 false 제거) */}
            {false && priceTable.length > 0 && (
                <div style={S.sidebarSection}>
                    <h4 style={S.sidebarSectionTitle}>단가표 {!isLoggedIn && <Lock size={12} color="#94a3b8" />}</h4>
                    {isLoggedIn ? (
                        <div style={S.sidebarPriceTable}>
                            <div style={S.priceTableHeader}>
                                <span>항목</span><span>단가</span><span>납기</span>
                            </div>
                            {priceTable.filter(p => p.price).map((row, i) => (
                                <div key={i} style={{ ...S.priceTableRow, backgroundColor: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                                    <span style={S.priceItemName}>{row.item || '-'}</span>
                                    <span style={S.priceItemValue}>{formatPrice(row.price)}</span>
                                    <span style={S.priceItemDays}>{row.deliveryDays ? `${row.deliveryDays}일` : '-'}</span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div style={{ padding: '14px', backgroundColor: '#f8fafc', borderRadius: 10, border: '1px dashed #c7d2fe', textAlign: 'center' }}>
                            <Lock size={16} color="#6366f1" style={{ marginBottom: 4 }} />
                            <div style={{ fontSize: 11, color: '#6366f1', fontWeight: 700 }}>가입 후 확인 가능</div>
                        </div>
                    )}
                </div>
            )}

            {/* 첫 거래 혜택(할인) — 의료법 가이드에 따라 제거 (2026-07-19) */}

            {/* CTA — 연결은 덴탈클러스터가 직접 받아 중개 (2026-08-14 대표 확정: 버튼 하나로 통합) */}
            <div style={{ marginBottom: 16 }}>
                {isLoggedIn ? (
                    <>
                        {/* 🌏 해외 열람자: 기존 번역 채팅 흐름 유지 */}
                        {isOverseasViewer ? (
                            <>
                                <button
                                    onClick={async () => {
                                        try {
                                            const { roomId } = await getOrCreateChatRoom(user.companyId || user.uid, labId, 'lab-profile');
                                            navigate(`/chat?room=${roomId}`);
                                        } catch (e) { alert('Could not open chat. Please try again.'); }
                                    }}
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: '13px', backgroundColor: '#4F52C7', color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer', marginBottom: 8 }}
                                >
                                    <MessageSquare size={18} /> Chat with this lab
                                </button>
                                <div style={{ padding: '12px 14px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 12.5, color: '#64748b', lineHeight: 1.6, marginBottom: 8 }}>
                                    Contact details are private. All communication happens in <b>translated chat</b> — use the Chat button on the Find Labs page.
                                </div>
                            </>
                        ) : connStatus === 'accepted' ? (
                            /* 이미 거래처로 연결된 경우: 연결됨 표시 유지 */
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '13px', backgroundColor: '#ecfdf5', color: '#059669', border: '1.5px solid #a7f3d0', borderRadius: 10, fontSize: 15, fontWeight: 700, marginBottom: 8 }}>
                                <CheckCircle size={18} /> 거래처로 연결됨
                            </div>
                        ) : inquirySent ? (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '14px', backgroundColor: '#ecfdf5', color: '#059669', border: '1.5px solid #a7f3d0', borderRadius: 10, marginBottom: 8 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 700 }}>
                                    <CheckCircle size={18} /> 문의가 접수되었습니다
                                </div>
                                <div style={{ fontSize: 12, color: '#047857', textAlign: 'center' }}>덴탈클러스터 담당자가 확인 후 곧 연락드립니다.</div>
                            </div>
                        ) : (
                            <>
                                <button
                                    onClick={handleDenclInquiry}
                                    disabled={inquirySending}
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: '14px', backgroundColor: '#4F52C7', color: '#fff', border: 'none', borderRadius: 10, fontSize: 15.5, fontWeight: 800, cursor: 'pointer', marginBottom: 8, opacity: inquirySending ? 0.6 : 1 }}
                                >
                                    <MessageSquare size={18} /> {inquirySending ? '접수 중…' : '덴클에 문의하기'}
                                </button>
                                <div style={{ padding: '10px 13px', backgroundColor: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 10, fontSize: 12, color: '#4338ca', lineHeight: 1.6, marginBottom: 8, textAlign: 'center' }}>
                                    연결·상담은 <b>덴탈클러스터가 직접</b> 도와드립니다.<br />문의하시면 담당자가 연락드립니다.
                                </div>
                            </>
                        )}
                    </>
                ) : (
                    <>
                        <div style={{ padding: '16px', backgroundColor: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 12, textAlign: 'center', marginBottom: 8 }}>
                            <Lock size={20} color="#6366f1" style={{ marginBottom: 6 }} />
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#3730a3', marginBottom: 4 }}>연락처 · 상세정보 확인</div>
                            <div style={{ fontSize: 11, color: '#6366f1', marginBottom: 12 }}>무료 가입 후 바로 확인 가능합니다</div>
                            <button onClick={() => window.location.href = '/signup'} style={{ width: '100%', padding: '10px', backgroundColor: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                                <UserPlus size={14} /> 무료 가입하기
                            </button>
                        </div>
                    </>
                )}
            </div>

            {/* Specialties */}
            {specialties.length > 0 && (
                <div style={S.sidebarSection}>
                    <h4 style={S.sidebarSectionTitle}>전문 분야</h4>
                    <div style={S.tagGroup}>
                        {specialties.map((s, i) => {
                            const name = typeof s === 'string' ? s : s.name || s;
                            return <span key={i} style={S.tag}>{name}</span>;
                        })}
                    </div>
                </div>
            )}

            {/* Compatible scanners */}
            {compatibleScanners.length > 0 && (
                <div style={S.sidebarSection}>
                    <h4 style={S.sidebarSectionTitle}>호환 스캐너</h4>
                    <div style={S.tagGroup}>
                        {compatibleScanners.map((s, i) => (
                            <span key={i} style={{ ...S.tag, backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }}>{s}</span>
                        ))}
                    </div>
                </div>
            )}

            {/* Delivery regions */}
            {deliveryRegions.length > 0 && (
                <div style={S.sidebarSection}>
                    <h4 style={S.sidebarSectionTitle}>배송 지역</h4>
                    <div style={S.tagGroup}>
                        {deliveryRegions.map((r, i) => (
                            <span key={i} style={{ ...S.tag, backgroundColor: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa' }}>{r}</span>
                        ))}
                    </div>
                </div>
            )}

            {/* 위치 + 연락처 — 해외 열람자에겐 비공개 (우회거래 방지) */}
            {address && !isOverseasViewer && (
                <div style={S.sidebarSection}>
                    <h4 style={S.sidebarSectionTitle}>연락처 {!isLoggedIn && <Lock size={12} color="#94a3b8" />}</h4>
                    {isLoggedIn ? (
                        <div style={S.contactList}>
                            <div style={S.contactRow}>
                                <MapPin size={15} color="#6366f1" />
                                <span style={S.contactText}>{address}</span>
                            </div>
                            {/* 직접 전화·이메일은 비공개 — 연결은 덴클 중개로 (2026-08-14) */}
                            <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.6, marginTop: 4 }}>
                                전화·이메일 등 상세 연락처는 <b>덴탈클러스터가 중개</b>하여 안내드립니다. 위 [덴클에 문의하기]를 눌러주세요.
                            </div>
                        </div>
                    ) : (
                        renderSignupBanner('연락처를 확인하려면 가입이 필요합니다')
                    )}
                </div>
            )}
        </div>
    );

    // ── Mobile bottom CTA bar ──
    const renderMobileBottomBar = () => {
        if (!isMobile) return null;
        return (
            <div style={S.mobileBottomBar}>
                {isLoggedIn ? (
                    <>
                        {!isOverseasViewer && (
                            inquirySent ? (
                                <div style={{ ...S.mobileBtnChat, flex: 1, backgroundColor: '#ecfdf5', color: '#059669' }}>
                                    <CheckCircle size={16} /> 문의 접수됨
                                </div>
                            ) : (
                                <button onClick={handleDenclInquiry} disabled={inquirySending} style={{ ...S.mobileBtnChat, flex: 1, backgroundColor: '#4F52C7', opacity: inquirySending ? 0.6 : 1 }}>
                                    <MessageSquare size={16} /> {inquirySending ? '접수 중…' : '덴클에 문의하기'}
                                </button>
                            )
                        )}
                    </>
                ) : (
                    <button style={{ ...S.mobileBtnChat, flex: 1, backgroundColor: '#6366f1' }} onClick={() => window.location.href = '/signup'}>
                        <UserPlus size={16} /> 무료 가입하고 연락처 확인
                    </button>
                )}
            </div>
        );
    };

    return (
        <div style={S.page}>
            {/* Back button */}
            <div style={S.backRow}>
                <button style={S.btnBack} onClick={() => navigate(-1)}>
                    <ArrowLeft size={18} /> {isOverseasViewer ? 'Back' : '뒤로가기'}
                </button>
            </div>
            {/* 🌏 해외 열람자 안내 배너 */}
            {isOverseasViewer && (
                <div style={{ maxWidth: 1080, margin: '0 auto 14px', padding: '12px 18px', background: '#EEF0FA', border: '1px solid #C7D2FE', borderRadius: 10, fontSize: 13.5, color: '#3730A3', lineHeight: 1.6 }}>
                    🌏 This profile is shown in Korean. {lab.introductionEn ? <b>English introduction: “{lab.introductionEn}”</b> : null} Use the <b>Chat</b> on the Find Labs page — messages are translated automatically.
                </div>
            )}

            <div style={isMobile ? S.layoutMobile : S.layout}>
                {/* Left column */}
                <div style={isMobile ? S.leftColMobile : S.leftCol}>
                    {/* 프로필 히어로 카드 */}
                    <div style={{
                        display: 'flex', gap: isMobile ? 16 : 24, marginBottom: 24,
                        padding: isMobile ? 16 : 24,
                        background: 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)',
                        border: '1px solid #e0e7ff',
                        borderRadius: 20,
                        alignItems: 'flex-start',
                    }}>
                        {/* 로고 */}
                        <div style={{
                            width: isMobile ? 72 : 100, height: isMobile ? 72 : 100,
                            borderRadius: 16, overflow: 'hidden',
                            border: '2px solid #fff',
                            boxShadow: '0 4px 16px rgba(99,102,241,0.12)',
                            flexShrink: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            backgroundColor: '#fff',
                        }}>
                            {profileImageUrl ? (
                                <img src={profileImageUrl} alt={businessName} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                            ) : (
                                <Building2 size={isMobile ? 28 : 40} color="#94a3b8" />
                            )}
                        </div>

                        {/* 이름 + 소개 */}
                        <div style={{ flex: 1 }}>
                            <h2 style={{ fontSize: isMobile ? 18 : 22, fontWeight: 900, color: '#0f172a', margin: '0 0 4px' }}>
                                {businessName}
                            </h2>
                            {lab.slogan && (
                                <p style={{ fontSize: 13, color: '#6366f1', fontWeight: 600, margin: '0 0 10px' }}>{lab.slogan}</p>
                            )}
                            {/* 핵심 정보 뱃지 */}
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: introduction ? 12 : 0 }}>
                                {foundingYear && (
                                    <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, backgroundColor: '#fff', border: '1px solid #e2e8f0', color: '#475569', fontWeight: 600 }}>
                                        설립 {foundingYear}년
                                    </span>
                                )}
                                {representative && (
                                    <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, backgroundColor: '#fff', border: '1px solid #e2e8f0', color: '#475569', fontWeight: 600 }}>
                                        대표 {representative}
                                    </span>
                                )}
                                {specialties.slice(0, 3).map(s => (
                                    <span key={s} style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, backgroundColor: '#eef2ff', border: '1px solid #c7d2fe', color: '#4f46e5', fontWeight: 600 }}>
                                        {typeof s === 'string' ? s : s.name}
                                    </span>
                                ))}
                            </div>
                            {/* 소개글 */}
                            {introduction && (
                                <p style={{
                                    fontSize: 13, color: '#475569', lineHeight: 1.8, margin: 0,
                                    whiteSpace: 'pre-line',
                                    display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                                }}>
                                    {introduction}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Mobile: show sidebar content here */}
                    {isMobile && renderSidebar()}

                    {/* 기공소 정보 (소개 제외 — 위에서 이미 표시) */}
                    <div style={S.tabContent}>
                        {renderInfoTab()}
                    </div>

                    {/* 포트폴리오 */}
                    <div style={S.tabContent}>
                        <h2 style={{ margin: '0 0 16px', fontSize: '20px', fontWeight: '800', color: '#0f172a' }}>포트폴리오</h2>
                        {renderPortfolioTab()}
                    </div>

                    {/* 포트폴리오 게시물 */}
                    {labPosts.length > 0 && (
                        <div style={S.tabContent}>
                            <h2 style={{ margin: '0 0 16px', fontSize: '20px', fontWeight: '800', color: '#0f172a' }}>
                                <Camera size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                                작업물 게시물
                                <span style={{ fontSize: '14px', color: '#6366f1', fontWeight: '600', marginLeft: '8px' }}>{labPosts.length}</span>
                            </h2>
                            {renderPostsTab()}
                        </div>
                    )}

                    {/* 리뷰 — 의료법 가이드(후기 표시 금지)에 따라 소유자에게만 노출 (데이터는 보존) */}
                    {user?.uid === labId && (
                        <div style={S.tabContent}>
                            <h2 style={{ margin: '0 0 16px', fontSize: '20px', fontWeight: '800', color: '#0f172a' }}>리뷰 <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '500' }}>(내 계정에만 표시됩니다)</span></h2>
                            {renderReviewTab()}
                        </div>
                    )}
                </div>

                {/* Right sidebar (desktop only) */}
                {!isMobile && (
                    <div style={S.rightCol}>
                        {renderSidebar()}
                    </div>
                )}
            </div>

            {/* Mobile bottom CTA */}
            {renderMobileBottomBar()}

            {/* Lightbox */}
            {lightboxImg && (
                <div style={S.lightboxOverlay} onClick={() => setLightboxImg(null)}>
                    <img src={lightboxImg} alt="확대보기" style={S.lightboxImg} onClick={e => e.stopPropagation()} />
                </div>
            )}

        </div>
    );
}

// ── Styles ──
const S = {
    page: {
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '20px',
        backgroundColor: '#fff',
        minHeight: '100vh',
    },
    loadingWrap: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
    },
    spinner: {
        width: '40px',
        height: '40px',
        border: '4px solid #e2e8f0',
        borderTop: '4px solid #6366f1',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        marginBottom: '16px',
    },

    // Back
    backRow: {
        marginBottom: '20px',
    },
    btnBack: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '10px 18px',
        backgroundColor: '#f8fafc',
        color: '#475569',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
    },

    // Layout
    layout: {
        display: 'flex',
        gap: '32px',
        alignItems: 'flex-start',
    },
    layoutMobile: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0',
    },
    leftCol: {
        flex: '0 0 68%',
        maxWidth: '68%',
    },
    leftColMobile: {
        width: '100%',
    },
    rightCol: {
        flex: '0 0 30%',
        maxWidth: '30%',
        position: 'sticky',
        top: '20px',
    },

    // Profile image
    profileImageWrap: {
        width: '100%',
        maxWidth: '280px',
        aspectRatio: '1',
        borderRadius: '20px',
        overflow: 'hidden',
        border: '1px solid #e2e8f0',
        marginBottom: '24px',
        backgroundColor: '#f8fafc',
    },
    profileImage: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
    profileImagePlaceholder: {
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f1f5f9',
    },

    // Tabs
    tabNav: {
        display: 'flex',
        borderBottom: '2px solid #e2e8f0',
        marginBottom: '0',
    },
    tabBtn: {
        padding: '14px 24px',
        border: 'none',
        borderBottom: '3px solid transparent',
        backgroundColor: 'transparent',
        fontSize: '15px',
        fontWeight: '600',
        color: '#94a3b8',
        cursor: 'pointer',
        marginBottom: '-2px',
        transition: 'all 0.2s',
    },
    tabBtnActive: {
        color: '#6366f1',
        borderBottomColor: '#6366f1',
    },
    tabContent: {
        padding: '24px 0',
    },

    // Sections
    section: {
        marginBottom: '32px',
    },
    sectionTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        margin: '0 0 16px',
        fontSize: '18px',
        fontWeight: '700',
        color: '#0f172a',
    },
    sectionText: {
        margin: 0,
        fontSize: '15px',
        color: '#475569',
        lineHeight: '1.8',
        whiteSpace: 'pre-wrap',
    },

    // Price table
    tableWrap: {
        overflowX: 'auto',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
    },
    th: {
        padding: '12px 16px',
        backgroundColor: '#f8fafc',
        fontSize: '13px',
        fontWeight: '700',
        color: '#64748b',
        textAlign: 'left',
        borderBottom: '1px solid #e2e8f0',
    },
    td: {
        padding: '12px 16px',
        fontSize: '14px',
        color: '#0f172a',
        borderBottom: '1px solid #f1f5f9',
    },

    // Benefit banner
    // Map
    mapContainer: {
        width: '100%',
        height: '300px',
        borderRadius: '14px',
        border: '1px solid #e2e8f0',
        overflow: 'hidden',
        backgroundColor: '#f1f5f9',
    },
    mapAddress: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        marginTop: '10px',
        fontSize: '13px',
        color: '#64748b',
    },

    benefitBanner: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '14px',
        padding: '20px',
        backgroundColor: '#fffbeb',
        border: '1px solid #fde68a',
        borderRadius: '14px',
        marginBottom: '24px',
    },
    benefitIcon: {
        width: '44px',
        height: '44px',
        borderRadius: '12px',
        backgroundColor: '#fef3c7',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    benefitLabel: {
        fontSize: '12px',
        fontWeight: '700',
        color: '#92400e',
        marginBottom: '4px',
    },
    benefitText: {
        fontSize: '15px',
        fontWeight: '600',
        color: '#78350f',
        lineHeight: '1.5',
    },

    // Portfolio
    portfolioGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '12px',
    },
    portfolioItem: {
        position: 'relative',
        paddingBottom: '100%',
        borderRadius: '12px',
        overflow: 'hidden',
        border: '1px solid #e2e8f0',
        cursor: 'pointer',
    },
    portfolioImg: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        transition: 'transform 0.2s',
    },
    portfolioOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0,0,0,0)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: 0,
        transition: 'all 0.2s',
    },

    // Empty state
    emptyState: {
        textAlign: 'center',
        padding: '60px 20px',
        backgroundColor: '#f8fafc',
        borderRadius: '16px',
        border: '2px dashed #e2e8f0',
    },
    emptyText: {
        marginTop: '12px',
        fontSize: '16px',
        fontWeight: '600',
        color: '#94a3b8',
    },

    // Sidebar
    sidebar: {
        backgroundColor: '#fff',
        borderRadius: '16px',
        border: '1px solid #e2e8f0',
        padding: '28px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    },
    sidebarMobile: {
        backgroundColor: '#fff',
        borderRadius: '16px',
        border: '1px solid #e2e8f0',
        padding: '20px',
        marginBottom: '24px',
    },
    sidebarName: {
        margin: '0 0 4px',
        fontSize: '22px',
        fontWeight: '800',
        color: '#0f172a',
    },
    sidebarSlogan: {
        margin: '0 0 12px',
        fontSize: '14px',
        color: '#6366f1',
        fontWeight: '600',
    },
    sidebarRating: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        marginBottom: '12px',
        cursor: 'pointer',
    },
    sidebarMeta: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '12px',
        marginBottom: '16px',
    },
    metaItem: {
        fontSize: '13px',
        color: '#64748b',
    },

    // Stats badges
    statsBadges: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        marginBottom: '20px',
    },
    statBadge: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 12px',
        backgroundColor: '#f0fdf4',
        border: '1px solid #bbf7d0',
        borderRadius: '8px',
        fontSize: '13px',
        fontWeight: '600',
        color: '#15803d',
    },

    // CTA
    ctaGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        marginBottom: '24px',
    },
    btnChatSidebar: {
        width: '100%',
        padding: '12px',
        backgroundColor: '#6366f1',
        color: '#fff',
        border: 'none',
        borderRadius: '10px',
        fontSize: '15px',
        fontWeight: '700',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        textDecoration: 'none',
        boxSizing: 'border-box',
    },
    btnPhoneSidebar: {
        flex: 1,
        padding: '12px',
        backgroundColor: '#f0fdf4',
        color: '#16a34a',
        border: '1px solid #bbf7d0',
        borderRadius: '12px',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        textDecoration: 'none',
    },

    // Sidebar price table
    sidebarPriceTable: {
        borderRadius: '10px',
        border: '1px solid #e2e8f0',
        overflow: 'hidden',
    },
    priceTableHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '8px 12px',
        backgroundColor: '#f1f5f9',
        fontSize: '11px',
        fontWeight: '700',
        color: '#64748b',
    },
    priceTableRow: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '8px 12px',
        borderTop: '1px solid #f1f5f9',
    },
    priceItemName: { fontSize: '12px', color: '#334155', fontWeight: '500', flex: 2 },
    priceItemValue: { fontSize: '12px', color: '#0f172a', fontWeight: '700', flex: 1, textAlign: 'right' },
    priceItemDays: { fontSize: '11px', color: '#94a3b8', flex: 0.5, textAlign: 'right' },

    // Sidebar benefit
    sidebarBenefit: {
        display: 'flex',
        gap: '10px',
        alignItems: 'flex-start',
        padding: '12px',
        backgroundColor: '#fffbeb',
        border: '1px solid #fde68a',
        borderRadius: '10px',
        marginBottom: '16px',
    },

    // Sidebar sections
    sidebarSection: {
        paddingTop: '20px',
        borderTop: '1px solid #f1f5f9',
        marginBottom: '8px',
    },
    sidebarSectionTitle: {
        margin: '0 0 10px',
        fontSize: '14px',
        fontWeight: '700',
        color: '#334155',
    },

    // Tags
    tagGroup: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px',
    },
    tag: {
        padding: '5px 12px',
        backgroundColor: '#eef2ff',
        color: '#6366f1',
        border: '1px solid #c7d2fe',
        borderRadius: '8px',
        fontSize: '12px',
        fontWeight: '600',
    },

    // Contact list
    contactList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
    },
    contactRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
    },
    contactLink: {
        fontSize: '14px',
        color: '#3b82f6',
        textDecoration: 'none',
        fontWeight: '500',
    },
    contactText: {
        fontSize: '14px',
        color: '#475569',
    },

    // Mobile bottom bar
    mobileBottomBar: {
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        display: 'flex',
        gap: '10px',
        padding: '12px 16px',
        backgroundColor: '#fff',
        borderTop: '1px solid #e2e8f0',
        boxShadow: '0 -2px 10px rgba(0,0,0,0.06)',
        zIndex: 100,
    },
    mobileBtnChat: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        padding: '14px',
        backgroundColor: '#6366f1',
        color: '#fff',
        border: 'none',
        borderRadius: '12px',
        fontSize: '15px',
        fontWeight: '700',
        cursor: 'pointer',
        textDecoration: 'none',
    },
    mobileBtnPhone: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        padding: '14px 20px',
        backgroundColor: '#f0fdf4',
        color: '#16a34a',
        border: '1px solid #bbf7d0',
        borderRadius: '12px',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer',
        textDecoration: 'none',
    },

    // Lightbox
    lightboxOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        cursor: 'pointer',
        padding: '40px',
    },
    lightboxImg: {
        maxWidth: '90%',
        maxHeight: '90%',
        objectFit: 'contain',
        borderRadius: '8px',
        cursor: 'default',
    },

    // ── Review styles ──
    reviewSummary: {
        textAlign: 'center',
        padding: '32px 20px',
        backgroundColor: '#f8fafc',
        borderRadius: '16px',
        border: '1px solid #e2e8f0',
        marginBottom: '24px',
    },
    starRow: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '2px',
    },
    btnWriteReview: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '10px 24px',
        backgroundColor: '#6366f1',
        color: '#fff',
        border: 'none',
        borderRadius: '10px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    reviewForm: {
        padding: '24px',
        backgroundColor: '#f8fafc',
        borderRadius: '16px',
        border: '1px solid #e2e8f0',
        marginBottom: '24px',
    },
    ratingRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '16px',
    },
    ratingLabel: {
        fontSize: '14px',
        fontWeight: '600',
        color: '#334155',
        minWidth: '90px',
    },
    ratingValue: {
        fontSize: '14px',
        fontWeight: '700',
        color: '#6366f1',
        minWidth: '20px',
    },
    reviewTextarea: {
        width: '100%',
        padding: '12px 16px',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        fontSize: '14px',
        color: '#0f172a',
        resize: 'vertical',
        outline: 'none',
        fontFamily: 'inherit',
        marginBottom: '16px',
        boxSizing: 'border-box',
        lineHeight: '1.6',
    },
    btnSubmitReview: {
        padding: '10px 24px',
        backgroundColor: '#6366f1',
        color: '#fff',
        border: 'none',
        borderRadius: '10px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    btnCancelReview: {
        padding: '10px 24px',
        backgroundColor: '#fff',
        color: '#64748b',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    reviewCard: {
        padding: '20px',
        backgroundColor: '#fff',
        borderRadius: '14px',
        border: '1px solid #e2e8f0',
        marginBottom: '12px',
    },
    ratingBadges: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        flexWrap: 'wrap',
    },
    ratingBadge: {
        fontSize: '12px',
        fontWeight: '600',
        color: '#64748b',
        padding: '2px 8px',
        backgroundColor: '#f1f5f9',
        borderRadius: '6px',
    },
    ratingBadgeDivider: {
        fontSize: '12px',
        color: '#d1d5db',
    },

    // 문의 모달
    inquiryModal: {
        backgroundColor: '#fff',
        borderRadius: '20px',
        padding: '28px',
        maxWidth: '480px',
        width: '90%',
        margin: '80px auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
    },
    inquiryInput: {
        width: '100%',
        padding: '12px 16px',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        fontSize: '14px',
        color: '#0f172a',
        outline: 'none',
        fontFamily: 'inherit',
        marginBottom: '10px',
        boxSizing: 'border-box',
    },
    inquiryTextarea: {
        width: '100%',
        padding: '12px 16px',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        fontSize: '14px',
        color: '#0f172a',
        resize: 'vertical',
        outline: 'none',
        fontFamily: 'inherit',
        marginBottom: '16px',
        boxSizing: 'border-box',
        lineHeight: '1.6',
    },
};

export default LabProfile;
